/**
 * Cute objects world · "Kitchen & bedroom": a cut-away little house — kitchen for levels 1-2 (tiles,
 * shelves, jars), bedroom for levels 3-4 (wallpaper, window, bookshelf); the trail is a ribbon rug.
 */
import { rolePalette } from '@sketchling/core';
import type { Layer } from '../../Figure';
import { ellipse } from '../../geom';
import { rect, rng, rrect, spread } from '../kit';
import type { WorldArt } from '../types';

const palette = rolePalette({
  wallKitchen: '#FCE8E2',
  tile: '#BFE0DC',
  tileLine: '#9CC9C3',
  wallBedroom: '#F6E3EF',
  dot: '#EFC7DC',
  shelf: '#C98E5E',
  cupboard: '#F0B9A0',
  floor: '#E8C9A8',
  floorLine: '#D2AE88',
  jar: '#FFF1DC',
  strawberry: '#E5596A',
  mint: '#9AD3BF',
  book1: '#E5596A',
  book2: '#5AAFD9',
  book3: '#F2C14E',
  window: '#CFE8F4',
  frame: '#FFFBF3',
});

export const CUTE_ART: WorldArt = {
  id: 'cuteObjects',
  palette,
  theme: {
    sky: '#FCE8E2',
    skyLow: '#FFF6EC',
    ink: '#5A3A36',
    label: '#FFFBF6',
    accents: ['#E5596A', '#9AD3BF', '#F2C14E'],
    trail: { kind: 'ribbon', color: '#F6D7C3', edge: '#E3A98E', deco: '#E5596A' },
    card: { bg: '#FCEDE7', border: '#F2D2C6', shadow: '#E8C2B3' },
    node: { ring: '#E5596A', done: '#4CC38A', disc: '#FFFBF6', locked: '#EADBD3' },
    raster: { mid: 0.7, amp: 0.05, scrim: 'rgba(255,246,236,0.12)', portrait: { mid: 0.84, amp: 0.04 } },
  },
  scene: ({ w, h, zones }) => {
    const s = h / 300;
    const R = rng(51);
    const L: Layer[] = [];
    const split = zones[2]?.[0] ?? w / 2;
    // walls
    L.push({ k: 'region', d: rect(-20, -20, split + 20, h + 40), fill: 'wallKitchen', shade: false, line: false });
    L.push({ k: 'region', d: rect(split, -20, w - split + 20, h + 40), fill: 'wallBedroom', shade: false, line: false });
    // kitchen tiles band + shelves with jars
    L.push({ k: 'region', d: rect(-20, h * 0.08, split + 20, h * 0.3), fill: 'tile', shade: false, line: false });
    for (let y = h * 0.08; y <= h * 0.38; y += 20 * s) L.push({ k: 'ink', d: `M-20 ${y}L${split} ${y}`, w: 0.4, color: 'tileLine', taper: 'none' });
    for (let x = 0; x < split; x += 20 * s) L.push({ k: 'ink', d: `M${x} ${h * 0.08}L${x} ${h * 0.38}`, w: 0.4, color: 'tileLine', taper: 'none' });
    for (const x of spread(40, split - 120 * s, 260 * s, 9)) {
      L.push({ k: 'region', d: rrect(x, h * 0.3, 150 * s, 8 * s, 2 * s), fill: 'shelf', shade: false });
      [0, 1, 2].forEach((k) => L.push({ k: 'region', d: rrect(x + 14 * s + k * 44 * s, h * 0.3 - 34 * s, 30 * s, 34 * s, 8 * s), fill: k === 1 ? 'mint' : 'jar', shade: 4 * s, rim: true }));
      L.push({ k: 'region', d: ellipse(x + 29 * s, h * 0.3 - 18 * s, 6 * s), fill: 'strawberry', shade: false });
    }
    // bedroom wallpaper dots, windows, bookshelves
    for (let y = 18 * s; y < h * 0.6; y += 30 * s) for (let x = split + 14 * s + ((y / (30 * s)) % 2) * 15 * s; x < w; x += 30 * s) L.push({ k: 'spot', d: ellipse(x, y, 3 * s), fill: 'dot', stage: 2 });
    spread(split + 60 * s, w - 80 * s, 300 * s, 10).forEach((x, i) => {
      if (i % 2 === 0) {
        L.push({ k: 'region', d: rrect(x, h * 0.06, 90 * s, 70 * s, 8 * s), fill: 'frame', shade: 4 * s });
        L.push({ k: 'region', d: rrect(x + 8 * s, h * 0.06 + 8 * s, 74 * s, 54 * s, 5 * s), fill: 'window', shade: false });
      } else {
        L.push({ k: 'region', d: rrect(x, h * 0.3, 140 * s, 8 * s, 2 * s), fill: 'shelf', shade: false });
        for (let k = 0; k < 6; k++) L.push({ k: 'region', d: rrect(x + 8 * s + k * 20 * s, h * 0.3 - (30 + (k % 3) * 6) * s, 16 * s, (30 + (k % 3) * 6) * s, 3 * s), fill: ['book1', 'book2', 'book3'][k % 3]!, shade: false });
      }
    });
    // cupboards / floor
    L.push({ k: 'region', d: rect(-20, h * 0.8, w + 40, h), fill: 'floor', shade: false, line: true });
    for (const x of spread(0, w, 70 * s, 12)) L.push({ k: 'ink', d: `M${x} ${h * 0.8}L${x - 20 * s} ${h}`, w: 0.5, color: 'floorLine', taper: 'none' });
    for (const x of spread(30, split - 60 * s, 220 * s, 13)) L.push({ k: 'region', d: rrect(x, h * 0.84, 110 * s, h * 0.2, 6 * s), fill: 'cupboard', shade: 5 * s });
    void R;
    return { vb: [w, h], layers: L, guides: [] };
  },
};
